// `/m#k=<token>`: one meeting's issues for someone the owner
// shared the link with. PUBLIC: no sign-in, no `/auth/me`, no cookie on the
// API calls (`linkMeetingApi.ts`).
//
// - The token is read from the fragment ONCE, kept in memory only (this
//   module, so a remount keeps it), and the fragment leaves the address bar
//   at once (`clearHash`: through the router when there is one, the pattern
//   of `../figma/useHereLocation.ts`). It never reaches a server URL, a
//   `Referer`, storage or a log.
// - The review is `MeetingReview` in mode "link": Accept, Edit and Decline
//   while an issue is a proposal, and the issues' open questions, open, with
//   one line on top ("3 Fragen sind noch offen.", which jumps to the first;
//   "Alle Fragen beantwortet." once none waits). Never Restore, retry, "Nicht
//   übernommen", "Beschlüsse" or the transcript (the server sends none).
// - "Verfeinern" is the GitLab sign-in with `return_to` = the issue's refine
//   page (the server allows `/focus/*` only), as a full page load.
// - A token the server refuses (unknown, expired, revoked, the owner gone)
//   is one 404; the page then says the link does not work.

import { useEffect, useState } from "react";

import {
  MeetingLinkIssueDto,
  type MeetingAnswerRequest,
  type MeetingIssuePatchRequest,
  type MeetingIssueRowDto,
  type MeetingLinkReviewDto,
} from "@loom/shared/meetings";
import { z } from "zod";

import { signInUrl } from "../../auth/useAuth";
import { useFormatDate } from "../../i18n/format";
import { usePlural, useT } from "../../i18n/index.js";
import { HttpError } from "../../lib/http";
import { FocusFrame, Headline, Help, Kicker, ThinkingDots } from "../ui";

import {
  answerLinkQuestion,
  editLinkIssue,
  linkIssueAction,
  linkTokenFromHash,
  readLinkReview,
} from "./linkMeetingApi";
import type { IssueAction } from "./meetingApi";
import { failureKeyOf, refineHrefOf } from "./meetingCopy";
import { openRefineCold } from "./MeetingPage";
import { openQuestionsOf } from "./MeetingQuestions";
import { MeetingReview, type RowError } from "./MeetingReview";

const DAY_FORMAT: Intl.DateTimeFormatOptions = { dateStyle: "long" };

// The token, in memory only. Set from the fragment the first time the page
// sees one; the address bar loses it right after.
let heldToken: string | null = null;

/** The token from `hash` when it carries one, else the one already held. */
export const takeLinkToken = (hash: string): string | null => {
  const fromHash = linkTokenFromHash(hash);
  if (fromHash !== null) heldToken = fromHash;
  return heldToken;
};

/** Tests only: forget the held token. */
export const forgetLinkToken = (): void => {
  heldToken = null;
};

/** The review as a row list `MeetingReview` renders (link rows are filed). */
export const linkRowOf = (i: MeetingLinkIssueDto, ordinal: number): MeetingIssueRowDto => ({
  ticketId: `link-${i.issueIid}`,
  ordinal,
  class: i.class,
  reason: i.reason,
  state: "filed",
  title: i.title,
  issueIid: i.issueIid,
  issueUrl: null,
  issueStatus: i.issueStatus,
  quotes: i.quotes,
  openQuestions: i.openQuestions,
  ...(i.questions !== undefined ? { questions: i.questions } : {}),
  existingIid: i.existingIid,
  failureCode: null,
  body: i.body,
  // "Gespeichert, nicht ins Ticket geschrieben" after a holder's answer.
  ...(i.bodySync !== undefined ? { bodySync: i.bodySync } : {}),
});

const FailureBody = z.object({ error: z.string(), issue: z.unknown().optional() });

interface LinkFailure {
  readonly status: number | null;
  readonly code: string | null;
  readonly issue: MeetingLinkIssueDto | null;
}

const linkFailureOf = (err: unknown): LinkFailure => {
  if (!(err instanceof HttpError)) return { status: null, code: null, issue: null };
  let body: z.infer<typeof FailureBody> | null = null;
  try {
    const parsed = FailureBody.safeParse(JSON.parse(err.body ?? ""));
    body = parsed.success ? parsed.data : null;
  } catch {
    body = null;
  }
  const issue = body?.issue === undefined ? null : MeetingLinkIssueDto.safeParse(body.issue);
  return {
    status: err.status,
    code: body?.error ?? null,
    issue: issue !== null && issue.success ? issue.data : null,
  };
};

type Load =
  | { readonly kind: "loading" }
  | { readonly kind: "invalid" }
  | { readonly kind: "failed" }
  | { readonly kind: "ready"; readonly review: MeetingLinkReviewDto };

export function MeetingLinkPage({
  clearHash,
  openCold = openRefineCold,
}: {
  /** Takes the fragment out of the address bar (the route passes the router's). */
  readonly clearHash: () => void;
  /** "Verfeinern": a full page load. A test seam only. */
  readonly openCold?: (href: string) => void;
}) {
  const t = useT();
  const tn = usePlural();
  const day = useFormatDate(DAY_FORMAT);
  const [token] = useState<string | null>(() =>
    takeLinkToken(typeof window === "undefined" ? "" : window.location.hash),
  );
  const [load, setLoad] = useState<Load>(() => (token === null ? { kind: "invalid" } : { kind: "loading" }));
  const [attempt, setAttempt] = useState(0);
  const [busyTicketId, setBusyTicketId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<RowError | null>(null);

  useEffect(() => {
    // Once: the fragment was read in the state initialiser above.
    if (typeof window !== "undefined" && window.location.hash !== "") clearHash();
  }, []);

  useEffect(() => {
    if (token === null) return undefined;
    let alive = true;
    readLinkReview(token)
      .then((review) => {
        if (alive) setLoad({ kind: "ready", review });
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setLoad({ kind: err instanceof HttpError && err.status === 404 ? "invalid" : "failed" });
      });
    return () => {
      alive = false;
    };
  }, [token, attempt]);

  if (load.kind === "loading") {
    return (
      <FocusFrame stepKey="meeting-link-loading">
        <ThinkingDots label={t("common.loading")} />
      </FocusFrame>
    );
  }
  if (load.kind === "invalid" || token === null) {
    return (
      <FocusFrame stepKey="meeting-link-invalid">
        <div className="focus-error" role="alert">
          {t("meetings.link.invalid")}
        </div>
      </FocusFrame>
    );
  }
  if (load.kind === "failed") {
    return (
      <FocusFrame stepKey="meeting-link-failed">
        <div className="focus-error" role="alert">
          {t("meetings.link.loadFailed")}
        </div>
        <button
          type="button"
          className="focus-btn is-ghost"
          onClick={() => {
            setLoad({ kind: "loading" });
            setAttempt((n) => n + 1);
          }}
        >
          {t("meetings.action.retry")}
        </button>
      </FocusFrame>
    );
  }
  const review = load.review;
  const rows = review.issues.map(linkRowOf);
  const projectId = review.projectId;

  const replace = (next: MeetingLinkIssueDto): void => {
    setLoad((prev) =>
      prev.kind !== "ready"
        ? prev
        : {
            kind: "ready",
            review: {
              ...prev.review,
              issues: prev.review.issues.map((i) => (i.issueIid === next.issueIid ? next : i)),
            },
          },
    );
  };

  /** A refusal of the token mid-page: the link stopped working. */
  const failed = (row: MeetingIssueRowDto, err: unknown): void => {
    const f = linkFailureOf(err);
    if (f.status === 404 && f.code === "not_found") {
      setLoad({ kind: "invalid" });
      return;
    }
    if (f.issue !== null) replace(f.issue);
    setRowError({
      ticketId: row.ticketId,
      key: failureKeyOf({ status: f.status, code: f.code, issue: null }),
    });
  };

  const runAction = (row: MeetingIssueRowDto, action: IssueAction): void => {
    // Restore is the owner's alone; the link has no such route.
    if (action === "restore" || busyTicketId !== null || row.issueIid === null) return;
    setBusyTicketId(row.ticketId);
    setRowError(null);
    linkIssueAction(token, row.issueIid, action)
      .then(replace)
      .catch((err: unknown) => failed(row, err))
      .finally(() => setBusyTicketId(null));
  };

  const saveEdit = async (row: MeetingIssueRowDto, patch: MeetingIssuePatchRequest): Promise<boolean> => {
    if (busyTicketId !== null || row.issueIid === null) return false;
    setBusyTicketId(row.ticketId);
    setRowError(null);
    try {
      replace(await editLinkIssue(token, row.issueIid, patch));
      return true;
    } catch (err) {
      failed(row, err);
      return false;
    } finally {
      setBusyTicketId(null);
    }
  };

  /** An open question answered through the link; true when saved. */
  const answer = async (
    row: MeetingIssueRowDto,
    field: string,
    body: MeetingAnswerRequest,
  ): Promise<boolean> => {
    if (row.issueIid === null) return false;
    setRowError(null);
    try {
      replace(await answerLinkQuestion(token, row.issueIid, field, body));
      return true;
    } catch (err) {
      const f = linkFailureOf(err);
      // A refusal that sends the issue back (`not_open`, `closed`) swaps the
      // row, which takes the question and its own failure line with it: the
      // row says why, as on the owner's page. Any other failure keeps the
      // question open with its "Antwort nicht gespeichert" line.
      if (f.issue !== null || (f.status === 404 && f.code === "not_found")) {
        failed(row, err);
      }
      return false;
    }
  };

  const asked = rows.reduce((n, r) => n + (r.questions ?? []).length, 0);
  const open = rows.reduce((n, r) => n + openQuestionsOf(r).length, 0);
  const jumpToFirst = (): void => {
    const first = document.querySelector<HTMLElement>("[data-question-open]");
    first?.scrollIntoView({ behavior: "smooth", block: "center" });
    first?.querySelector<HTMLElement>("button, textarea, input")?.focus();
  };

  return (
    <FocusFrame stepKey="meeting-link" wide>
      <Kicker>{t("meetings.link.kicker", { date: day(review.date) })}</Kicker>
      <Headline>{review.title ?? t("meetings.row.untitled")}</Headline>
      {review.projectName !== null && (
        <p className="meet-meta">{t("meetings.link.project", { project: review.projectName })}</p>
      )}
      <Help>{t("meetings.link.asOwner")}</Help>
      {asked > 0 &&
        (open > 0 ? (
          <button type="button" className="focus-deeplink meet-questions-top" onClick={jumpToFirst}>
            {tn("plural.meetingQuestionsOpenLine", open)}
          </button>
        ) : (
          <p className="meet-meta" role="status">
            {t("meetings.question.allAnswered")}
          </p>
        ))}
      <section className="meet-section">
        <h2 className="meet-section-title">{t("meetings.detail.issues")}</h2>
        {rows.length === 0 ? (
          <Help>{t("meetings.link.noIssues")}</Help>
        ) : (
          <MeetingReview
            mode="link"
            issues={rows}
            busyTicketId={busyTicketId}
            rowError={rowError}
            onAction={runAction}
            onSaveEdit={saveEdit}
            onAnswer={answer}
            {...(projectId === null
              ? {}
              : {
                  onRefine: (row: MeetingIssueRowDto) => {
                    if (row.issueIid !== null) {
                      openCold(signInUrl(refineHrefOf(projectId, row.issueIid)));
                    }
                  },
                })}
          />
        )}
      </section>
    </FocusFrame>
  );
}
